import { message, Divider, Empty, Table } from 'antd'
import classnames from 'classnames'
import PropTypes from 'prop-types'
import { useCallback, useState, useMemo } from 'react'
import { createUseStyles } from 'react-jss'
import api from '../../api'
import { useAsync } from '../../hooks'
import { getPaginationHeaders } from '../../redux/utils'
import { getApiRoute } from '../../lib/commentable-types'
import CommentForm from './CommentForm'
import useColumns from './useColumns'

const useStyles = createUseStyles({
  body: {
    '& .ant-table-tbody > tr > td': {
      padding: '0 16px', // Reduce padding for the body cells
    },
  },
})

export default function CommentsTable({
  collectionName,
  containerClassName = undefined,
  id,
  practiceId = null,
}) {
  const classes = useStyles()
  const [page, setPage] = useState(1)
  const fetchAllComments = useCallback(
    () => api.comments.listAll(collectionName, id, page),
    [collectionName, id, page],
  )
  const { pending, value, execute } = useAsync(fetchAllComments)
  const comments = useMemo(() => value ? value.body : [], [value])
  const handlePost = useCallback(
    async (comment, { resetForm }) => {
      try {
        await api.comments.create(collectionName, id, comment)
        message.success('Comment posted')
        resetForm()
      } catch (e) {
        message.error(
          'There was an error posting your comment. Please try again',
        )
      }
      await execute()
    },
    [collectionName, execute, id],
  )
  const handleUpdate = useCallback(
    async comment => {
      try {
        const fullComment = comments.find(item => item.id === comment.id)
        const collection = getApiRoute(fullComment.commentableType, practiceId)

        await api.comments.update(
          collection,
          fullComment.commentableId,
          comment,
        )
        message.success('Comment updated')
      } catch (e) {
        message.error(
          'There was an error updating your comment. Please try again',
        )
      }
      await execute()
    },
    [execute, comments, practiceId],
  )
  const handleDelete = useCallback(
    async commentId => {
      try {
        const comment = comments.find(item => item.id === commentId)

        const collection = getApiRoute(comment.commentableType, practiceId)

        await api.comments.destroy(collection, comment.commentableId, commentId)
        message.success('Comment deleted')
      } catch (e) {
        message.error(
          'There was an error deleting your comment. Please try again',
        )
      }
      await execute()
    },
    [execute, comments, practiceId],
  )

  const { pageSize, total } = value ? getPaginationHeaders(value.headers) : {}

  const columns = useColumns(handleUpdate, handleDelete, practiceId)

  return (
    <div className={classnames('comments', containerClassName)}>
      {Boolean(total) && (
        <Table
          rowKey={record => record.id}
          columns={columns}
          dataSource={comments}
          loading={pending}
          className={classes.body}
          // A numeric min-width lets the table fill the full container width on
          // desktop while still scrolling horizontally on a phone, where the
          // Datetime/comment/source columns would otherwise be cut off.
          scroll={{ x: 640 }}
          pagination={
            total > pageSize && {
              current: page,
              pageSize,
              onChange: setPage,
              total,
            }
          }
        />
      )}
      {!total && (
        <Empty
          description="No comments yet"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}
      <Divider />
      <CommentForm onSubmit={handlePost} />
    </div>
  )
}

CommentsTable.propTypes = {
  id: PropTypes.string.isRequired,
  collectionName: PropTypes.string.isRequired,
  containerClassName: PropTypes.string,
  practiceId: PropTypes.string,
}
